import React, { useRef, useEffect, useState, useContext } from "react";
import { useAnimation, useMotionValue } from "framer-motion";
import { Button } from "../../components/atoms";
import { getLogoURL } from "../../utils/general";
import ModalWinner from "../../components/blocks/modal-winner";
import { ImSpinner3 } from "react-icons/im";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { PageWrapper, RouletteWheel } from "../../components/blocks";
import tickSound from "../../assets/sounds/tick_ruleta.mp3";
import winnerSound from "../../assets/sounds/winner.mp3";
import { useNavigate, useParams } from "react-router-dom";
import { SiteContext } from "../../context/SiteContext";
import useMediaQuery from "../../hooks/useMediaQuery";

const JuegoRuleta = () => {
  // detectar ancho de pantalla
  const isXs    = useMediaQuery("(max-width: 375px)");  // pantallas ≤375px
  const isSm    = useMediaQuery("(max-width: 425px)");  // pantallas ≤425px
  const isMdUp  = useMediaQuery("(min-width: 768px)");  // pantallas ≥768px

  // calcular size según breakpoints
  let wheelSize;
  if (isXs)           wheelSize = 300;
  else if (isSm)      wheelSize = 350;
  else if (isMdUp)    wheelSize = 500;
  else                wheelSize = 350;  // entre 426px y 767px

  const { id } = useParams();
  const { sala } = useContext(SiteContext);
  const navigate = useNavigate();

  const canvasRef = useRef(null);
  const rotate = useMotionValue(0);
  const controls = useAnimation();
  const [spinning, setSpinning] = useState(false);
  const [options, setOptions] = useState([]);
  const [current, setCurrent] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const tickAudioRef = useRef(null);
  const winnerAudioRef = useRef(null);

  const API_HEADERS = {
    token: "7e647cb7-fb84-482e-acab-400465d154c0",
  };

  const { data: dataRoulette, isLoading } = useQuery({
    queryKey: ["roulette-prizes", id],
    queryFn: async () => {
      const res = await axios.get(`https://api5.wargos.cloud/xsorteos/roulette/${id}`);
      return res.data;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (!dataRoulette) return;
    const prizes = dataRoulette?.prizes ?? [];

    // Expandir premios según 'repeat'
    let expanded = [];
    prizes.forEach((p) => {
      for (let i = 0; i < (p.repeat || 1); i++) {
        expanded.push(p.name);
      }
    });

    // Mezclar aleatoriamente asegurando que no haya dos iguales juntos
    function shuffleNoAdjDuplicates(arr) {
      let tries = 0;
      while (tries < 1000) {
        let shuffled = arr.slice().sort(() => Math.random() - 0.5);
        let valid = true;
        for (let i = 1; i < shuffled.length; i++) {
          if (shuffled[i] === shuffled[i - 1]) {
            valid = false;
            break;
          }
        }
        // También verificar el primero y último (ruleta es circular)
        if (shuffled.length > 1 && shuffled[0] === shuffled[shuffled.length - 1]) valid = false;
        if (valid) return shuffled;
        tries++;
      }
      return arr;
    }

    const names = shuffleNoAdjDuplicates(expanded);
    setOptions(names);
    if (names.length > 0) setCurrent(names[0]);
  }, [dataRoulette]);

  // 2) Al montar, dibujo + posición inicial RANDOM
  useEffect(() => {
    if (!options.length) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const { width, height } = canvas;
    const cx = width / 2,
      cy = height / 2;
    const r = Math.min(cx, cy);
    const sliceAngle = (2 * Math.PI) / options.length;

    // Escala relativa a tu diseño original
    const SCALE = width / 600; // 600 era tu innerSize base
    const FONT_SIZE = Math.round(20 * SCALE);
    const TEXT_OFFSET = 25 * SCALE;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.translate(cx, cy);
    // Construyo los colores alternados
    const colors = options.map(
      (_, i) => (i % 2 === 0 ? "#000" /* negro */ : "#ffffff") /* blanco */
    );

    options.forEach((opt, i) => {
      const start = i * sliceAngle,
        end = start + sliceAngle;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, r, start, end);
      ctx.fillStyle = colors[i];
      ctx.fill();
      const MAX_CHARS = 12;
      const displayText = opt.length > MAX_CHARS ? opt.slice(0, MAX_CHARS) + "..." : opt;

      ctx.save();
      ctx.rotate(start + sliceAngle / 2);
      ctx.fillStyle = i % 2 === 0 ? "#fff" : "#000";
      ctx.font = `bold ${FONT_SIZE}px Arial`;
      ctx.textAlign = "right";
      ctx.fillText(displayText, r - TEXT_OFFSET, 0);
      ctx.restore();
    });

    // inicial aleatorio
    rotate.set(Math.random() * 360);
  }, [rotate, options,  wheelSize]);

  // Actualiza `current` bajo la flecha en tiempo real
  useEffect(() => {
    if (!options.length) return;
    const sliceDeg = 360 / options.length;
    let lastIdx = null;
    const unsub = rotate.onChange((v) => {
      const ang = (((-90 - v) % 360) + 360) % 360;
      const idx = Math.floor(ang / sliceDeg);
      setCurrent(options[idx]);
      if (lastIdx !== null && idx !== lastIdx && tickAudioRef.current) {
        // Reproducir sonido en cada cambio de slice
        tickAudioRef.current.currentTime = 0;
        tickAudioRef.current.play();
      }
      lastIdx = idx;
    });
    return unsub;
  }, [rotate, options]);

  useEffect(() => {
    tickAudioRef.current = new window.Audio(tickSound);
    tickAudioRef.current.volume = 0.5;
  }, []);

  useEffect(() => {
    winnerAudioRef.current = new window.Audio(winnerSound);
    winnerAudioRef.current.volume = 0.7;
  }, []);

  useEffect(() => {
    if (modalOpen && winnerAudioRef.current) {
      winnerAudioRef.current.currentTime = 0;
      winnerAudioRef.current.play();
    }
  }, [modalOpen]);

  // Spin de 10s with cubic-bezier(0.6,0,0,1)
  const girar = async () => {
    if (spinning || !options.length) return;
    setSpinning(true);

    let prize = current;
    try {
      const res = await fetch("https://devices5.wargos.cloud/xsorteos/roulette/spin", {
        method: "POST",
        headers: {
          ...API_HEADERS,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ rouletteId: dataRoulette.id }),
      });
      const data = await res.json();
      prize = data?.name ?? data?.prize ?? prize;
    } catch (e) {
      console.error(e);
    }

    const idx = options.indexOf(prize);
    setCurrent(prize);

    const sliceDeg = 360 / options.length;
    const midpoint = idx >= 0 ? idx * sliceDeg + sliceDeg / 2 : 0;
    const offset = 270 - midpoint;
    const base = 18 * 360 + offset;
    const target = rotate.get() + base - (rotate.get() % 360);

    controls
      .start({
        rotate: target,
        transition: {
          duration: 10,
          ease: [0.6, 0, 0, 1],
        },
      })
      .then(() => {
        setSpinning(false);
        setModalOpen(true);
      });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-10 font-bold text-neutral-950">
        <ImSpinner3 size={48} className="mb-4 animate-spin text-neutral-500" />
        <span>Cargando premios...</span>
      </div>
    );
  }

  const goBack = () => {
    navigate("/roulette-list");
  };

  return (
    <PageWrapper>
      <div className="flex flex-col items-center justify-center w-full min-h-screen gap-8 p-6">
        <div
          onClick={() => goBack()}
          className="flex items-center justify-center transition-all cursor-pointer hover:scale-[95%]"
        >
          {sala ? (
            <img className="w-auto max-h-[80px]" src={getLogoURL(sala.logo)} alt="logo" />
          ) : (
            <div className="h-[80px] w-[200px] bg-white/50 animate-pulse" />
          )}
        </div>
        <div className="flex flex-col items-center justify-center h-full gap-12 font-bold text-neutral-950">
          <span className="text-4xl font-extrabold uppercase font-mont">{dataRoulette?.name}</span>
          <RouletteWheel canvasRef={canvasRef} rotate={rotate} controls={controls} size={wheelSize} />
          <Button onClick={girar} disabled={spinning || !options.length}>
            {spinning ? (
              <ImSpinner3 size={24} className="animate-spin text-neutral-500" />
            ) : (
              "JUGAR"
            )}
          </Button>
          {modalOpen && <ModalWinner prize={current} onClose={() => setModalOpen(false)} />}
        </div>
      </div>
    </PageWrapper>
  );
};

export default JuegoRuleta;
